/**
 * TickerAutocomplete (Patch BJ): filterable picker for selecting a
 * security ticker. Two paths to a result:
 *
 *   1. Fast path: bundled list (src/data/tickers.ts, ~200 hand-curated
 *      Yahoo-resolvable entries). User types, list filters live, picks
 *      a match. Free, offline-capable, instant.
 *
 *   2. Long-tail path: "Custom..." button at the bottom of the dropdown
 *      opens a free-text field. User types any ticker. On commit, the
 *      parent component is expected to call /api/etf-price to validate;
 *      we don't do the network call here, just hand the symbol up.
 *
 * Why this isn't a native <select>: native selects can't render with a
 * filter input. Native datalist can but is laggy with hundreds of entries
 * and loses the kind-routing affordances. Custom popover gives us the
 * right ergonomics without the build cost of a full search-as-you-type
 * component.
 *
 * Accessibility: keyboard navigable (up/down to move highlight, enter to
 * select, escape to close). Click-outside closes via a document listener
 * attached only while the popover is open.
 *
 * Color-coded kind badge on each row: makes it visually obvious whether
 * a ticker is a stock, ETF, mutual fund, etc. Drives the eventual asset
 * class for sub-card routing.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { BundledTicker } from '../data/tickers.js';
import { searchBundledTickers } from '../data/tickers.js';
import type { SecurityAssetClass } from '../engine/types.js';

interface TickerAutocompleteProps {
  /** Restrict the dropdown to specific asset classes. e.g. the Metals
   *  sub-card passes ['metal_etf', 'spot_metal'] so the user only sees
   *  metal-related options. Omit for "all assets" (used by Stocks & ETFs). */
  kindFilter?: SecurityAssetClass[];
  /** Called when the user picks a bundled ticker. */
  onSelect: (ticker: BundledTicker) => void;
  /** Called when the user commits a custom (non-bundled) ticker via the
   *  Custom... path. Parent is responsible for validating via the price
   *  proxy and showing an error if it doesn't resolve. */
  onCustom: (symbol: string) => void;
  /** Optional placeholder for the input field. */
  placeholder?: string;
}

const KIND_LABELS: Record<SecurityAssetClass, string> = {
  stock: 'Stock',
  etf: 'ETF',
  mutual_fund: 'MF',
  bond_etf: 'Bond',
  commodity_etf: 'Comm',
  crypto_etf: 'Crypto',
  metal_etf: 'Metal',
  spot_metal: 'Spot',
};

// Per-kind badge background. Subtle, accent-tinted so the user sees the
// category at a glance without each badge fighting for attention.
const KIND_COLORS: Record<SecurityAssetClass, string> = {
  stock: 'var(--accent)',
  etf: 'var(--pos)',
  mutual_fund: '#9b8aff',
  bond_etf: '#6e90c4',
  commodity_etf: '#c79a45',
  crypto_etf: 'var(--accent)',
  metal_etf: '#d4af37',
  spot_metal: '#d4af37',
};

export function TickerAutocomplete(props: TickerAutocompleteProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(0);
  const [customMode, setCustomMode] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(
    () => searchBundledTickers(query, props.kindFilter),
    [query, props.kindFilter],
  );

  // Click-outside closes the popover. Document listener only attached
  // while open to avoid noise. Pattern matches MobileNav and the existing
  // popover work elsewhere in the codebase.
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setCustomMode(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Reset highlight when the filter shrinks or changes.
  useEffect(() => {
    setHighlightedIdx(0);
  }, [query, filtered.length]);

  function commitBundled(t: BundledTicker): void {
    props.onSelect(t);
    setQuery('');
    setOpen(false);
    setCustomMode(false);
  }

  function commitCustom(): void {
    const sym = query.trim().toUpperCase();
    if (sym.length === 0) return;
    props.onCustom(sym);
    setQuery('');
    setOpen(false);
    setCustomMode(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (customMode) {
        commitCustom();
      } else if (filtered[highlightedIdx]) {
        commitBundled(filtered[highlightedIdx]);
      } else {
        // No bundled match for the typed query; offer custom path.
        commitCustom();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setCustomMode(false);
    }
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block', minWidth: 280 }}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={props.placeholder ?? 'Type ticker or name...'}
        style={{ width: '100%' }}
      />
      {open ? (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 100,
            marginTop: 4,
            background: 'var(--bg-elev)',
            border: '1px solid var(--rule-strong)',
            borderRadius: 4,
            maxHeight: 360,
            overflowY: 'auto',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          }}
        >
          {filtered.length === 0 && query.trim() !== '' ? (
            <div
              style={{
                padding: 'var(--gap-s) var(--gap-m)',
                fontSize: 'var(--fs-small)',
                color: 'var(--text-soft)',
              }}
            >
              No bundled match. Press Enter to use{' '}
              <span className="mono" style={{ color: 'var(--accent)' }}>{query.trim().toUpperCase()}</span>
              {' '}as a custom ticker (we'll validate via Yahoo on save).
            </div>
          ) : (
            filtered.map((t, i) => (
              <button
                key={t.symbol}
                onMouseDown={e => { e.preventDefault(); commitBundled(t); }}
                onMouseEnter={() => setHighlightedIdx(i)}
                className="ghost"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '6px 10px',
                  textAlign: 'left',
                  background: i === highlightedIdx ? 'rgba(255,255,255,0.06)' : 'transparent',
                  border: 'none',
                  borderRadius: 0,
                  fontSize: 'var(--fs-small)',
                  cursor: 'pointer',
                }}
              >
                <span
                  className="mono"
                  style={{
                    minWidth: 70,
                    fontWeight: 500,
                    color: 'var(--text)',
                  }}
                >
                  {t.symbol}
                </span>
                <span style={{ flex: 1, color: 'var(--text-soft)' }}>{t.name}</span>
                <span
                  style={{
                    fontSize: 'var(--fs-micro)',
                    padding: '1px 6px',
                    borderRadius: 3,
                    background: KIND_COLORS[t.kind],
                    color: 'var(--bg)',
                    fontWeight: 500,
                  }}
                >
                  {KIND_LABELS[t.kind]}
                </span>
              </button>
            ))
          )}
          <div
            style={{
              borderTop: '1px solid var(--rule)',
              padding: '6px 10px',
              fontSize: 'var(--fs-micro)',
              color: 'var(--text-faint)',
            }}
          >
            {filtered.length === 50 ? 'Showing first 50, refine your search to narrow' : `${filtered.length} match${filtered.length === 1 ? '' : 'es'}`}
            {' \u00B7 '}
            Custom tickers go through Yahoo on save
          </div>
        </div>
      ) : null}
    </div>
  );
}
